import { useCallback, useEffect, useState } from "react";
import {
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { useTeleconsultoriaAvailability } from "../hooks/useTeleconsultoriaAvailability";
import { useTeleconsultoriaPresencePublisher } from "../presence/useTeleconsultoriaPresencePublisher";
import { listReceivedTeleconsultoriaInvites } from "../services/listReceivedInvites";
import type { TeleconsultoriaReceivedInvite } from "../types";
import { ReceivedInvitesList } from "./invites/ReceivedInvitesList";

export function TeleconsultoriaAvailabilityPanel() {
  const { user } = useSessionContext();
  const { loading, saving, available, setAvailable } =
    useTeleconsultoriaAvailability();
  // Peer tc-{cpf} só enquanto este painel está aberto e o switch está ligado.
  useTeleconsultoriaPresencePublisher(available);
  const [invites, setInvites] = useState<TeleconsultoriaReceivedInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  /** Hora da última carga bem-sucedida, exibida no cabeçalho da lista. */
  const [invitesUpdatedAt, setInvitesUpdatedAt] = useState<Date | null>(null);

  const loadInvites = useCallback(async () => {
    if (!user?.username) return;
    setInvitesLoading(true);
    try {
      const rows = await listReceivedTeleconsultoriaInvites(
        `Practitioner/${oids.cpf}-${user.username}`,
      );
      setInvites(rows);
      setInvitesUpdatedAt(new Date());
    } catch (error) {
      console.error("TeleconsultoriaAvailabilityPanel", error);
      toast.error("Não foi possível carregar os convites.");
      setInvites([]);
    } finally {
      setInvitesLoading(false);
    }
  }, [user?.username]);

  useEffect(() => {
    void loadInvites();
  }, [loadInvites]);

  return (
    <Stack spacing={2} width="100%">
      <Paper variant="outlined" sx={{ p: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={available}
              disabled={loading || saving}
              onChange={(_, checked) => void setAvailable(checked)}
            />
          }
          label={available ? "Disponível" : "Indisponível"}
        />
        <Typography variant="body2" color="text.secondary">
          Com a disponibilidade ligada, outros profissionais podem convidar você
          para a videochamada de um atendimento. Você não entra na tela de
          atendimento.
        </Typography>
        {saving && <CircularProgress size={22} sx={{ mt: 1 }} />}
      </Paper>

      {/* Lista agrupada por momento (agora, hoje, próximos, anteriores). */}
      <ReceivedInvitesList
        invites={invites}
        loading={invitesLoading}
        available={available}
        onRefresh={() => void loadInvites()}
        lastUpdatedAt={invitesUpdatedAt}
        refreshDisabled={!user?.username}
      />
    </Stack>
  );
}
